import React, { useCallback, useEffect, useState } from "react";
import api, { fmtDate, fmtTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileText, ChevronDown, ChevronUp } from "lucide-react";

const hora = (s) => fmtTime(s);

export function AuditLog({ entidadeId }) {
  const [rows, setRows] = useState([]);
  const [filtros, setFiltros] = useState({ usuarios: [], entidades: [] });
  const [q, setQ] = useState("");
  const [usuario, setUsuario] = useState("todos");
  const [entidade, setEntidade] = useState("todas");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/audit/filtros").then((r) => setFiltros(r.data)).catch(() => {}); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ q: entidadeId || q, usuario: usuario === "todos" ? "" : usuario, entidade: entidade === "todas" ? "" : entidade, de, ate });
    try { const { data } = await api.get(`/audit?${p}`); setRows(data); } catch { /* sem permissão */ }
    setLoading(false);
  }, [q, usuario, entidade, de, ate, entidadeId]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-3" data-testid="audit-log">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Input placeholder="Buscar ação / ID..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-q" className="col-span-2 md:col-span-1" />
        <Select value={usuario} onValueChange={setUsuario}>
          <SelectTrigger data-testid="audit-usuario"><SelectValue placeholder="Usuário" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos os usuários</SelectItem>{filtros.usuarios.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={entidade} onValueChange={setEntidade}>
          <SelectTrigger data-testid="audit-entidade"><SelectValue placeholder="Entidade" /></SelectTrigger>
          <SelectContent><SelectItem value="todas">Todas as entidades</SelectItem>{filtros.entidades.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={de} onChange={(e) => setDe(e.target.value)} data-testid="audit-de" />
        <Input type="date" value={ate} onChange={(e) => setAte(e.target.value)} data-testid="audit-ate" />
      </div>
      <div className="trt-card overflow-hidden">
        <div className="px-4 py-3 font-bold border-b flex items-center justify-between">
          <span className="flex items-center gap-2"><FileText className="w-4 h-4 gold-text" /> Registro de Alterações</span>
          <span className="text-xs text-slate-500 font-normal" data-testid="audit-count">{rows.length} registro(s)</span>
        </div>
        {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div>
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left px-4 py-2">Data / Hora</th><th className="text-left px-4 py-2">Usuário</th>
              <th className="text-left px-4 py-2">Ação</th><th className="text-left px-4 py-2">Entidade</th><th className="px-2" /></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={5} className="py-6 text-center text-slate-400">Sem registros.</td></tr>
                : rows.map((a) => (
                  <React.Fragment key={a.id}>
                    <tr className="border-t hover:bg-slate-50 cursor-pointer" data-testid={`audit-row-${a.id}`} onClick={() => setOpen(open === a.id ? null : a.id)}>
                      <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{fmtDate(a.data)} <span className="mono text-xs">{hora(a.data)}</span></td>
                      <td className="px-4 py-2 font-medium">{a.usuario}<div className="text-[11px] text-slate-400">{a.usuario_email}</div></td>
                      <td className="px-4 py-2">{a.acao}</td>
                      <td className="px-4 py-2 text-slate-500">{a.entidade}<div className="text-[11px] text-slate-400 mono">{(a.entidade_id || "").slice(0, 8)}</div></td>
                      <td className="px-2 text-slate-400">{(a.antes || a.depois) && (open === a.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}</td>
                    </tr>
                    {open === a.id && (a.antes || a.depois) && (
                      <tr className="bg-slate-50/60" data-testid={`audit-diff-${a.id}`}><td colSpan={5} className="px-4 py-3">
                        <Diff antes={a.antes} depois={a.depois} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </table></div>}
      </div>
    </div>
  );
}

const fmtVal = (v) => (v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

export function Diff({ antes, depois }) {
  const keys = Array.from(new Set([...Object.keys(antes || {}), ...Object.keys(depois || {})]));
  if (keys.length === 0) return <span className="text-xs text-slate-400">Sem detalhes.</span>;
  return (
    <div className="grid gap-1 text-xs">
      {keys.map((k) => {
        const a = antes?.[k], d = depois?.[k];
        const changed = JSON.stringify(a) !== JSON.stringify(d);
        return (
          <div key={k} className="grid grid-cols-[8rem_1fr_1fr] gap-2 items-start">
            <span className="text-slate-500 font-medium truncate">{k}</span>
            <span className={`mono break-all ${changed && antes ? "text-red-600 line-through" : "text-slate-500"}`}>{fmtVal(a)}</span>
            <span className={`mono break-all ${changed ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>{fmtVal(d)}</span>
          </div>
        );
      })}
    </div>
  );
}
